/**
 * Which address parts a listing must carry before it can go into a signed
 * document — and which of them the realtor is allowed to leave blank.
 *
 * A contract identifies the property. "רחוב הרצל, תל אביב" doesn't: it could be
 * any of two hundred flats. So the send form treats street / city / house
 * number as mandatory, the apartment number as mandatory *for property types
 * that have one*, and the floor as always-optional (nice to have, never worth
 * blocking a send over).
 *
 * The per-type rules deliberately mirror the ones the listing dialog already
 * applies (`showApartmentNumberInput`, `ListingDialogInputFloors`) so a realtor
 * sees the same fields in both places for the same property.
 */

import { showApartmentNumberInput } from "../listings/modal/form/ListingDialogInputApartmentNumber";
import { reverseListingTypeLabel } from "../../../utils/listingsMatch/parser";
import type { ListingType } from "../../../@types/listings/unions";
import type { ProcessedListingData, PropertyType } from "../../../@types/sharedSchema";

/** The address parts the send form can ask the realtor to fill. */
export const LISTING_ADDRESS_PART_KEYS = [
  "street",
  "houseNumber",
  "apartmentNumber",
  "floor",
  "city"
] as const;

export type ListingAddressPartKey = typeof LISTING_ADDRESS_PART_KEYS[number];

/**
 * Form-shaped address parts — every value is a string because they come
 * straight out of TextFields. `""` means "empty", which for an override means
 * "intentionally cleared for this document".
 */
export type ListingAddressParts = Record<ListingAddressPartKey, string>;

/**
 * - `required` — send is gated until it has a value.
 * - `optional` — offered, never gates.
 * - `hidden`   — meaningless for this property type; not rendered at all.
 */
export type ListingAddressPartRequirement = "required" | "optional" | "hidden";

export type ListingAddressRequirements = Record<
  ListingAddressPartKey,
  ListingAddressPartRequirement
>;

/**
 * Hebrew `propertyType` label (what Algolia/Firestore store on a listing) →
 * internal `ListingType` (what the per-type form rules are keyed by).
 * Returns `null` for a missing label or one outside our taxonomy — Yad2 and
 * Madlan occasionally carry types we don't model.
 */
export function listingTypeFromPropertyType(
  propertyType: string | undefined | null
): ListingType | null {
  if (!propertyType) return null;
  return reverseListingTypeLabel[propertyType as PropertyType] ?? null;
}

/**
 * Property types with no floor at all. Mirrors `ListingDialogInputFloors`,
 * which bails out early for plots of land. Spelled out here rather than
 * imported from `utils/listings/helper` so this module stays free of React
 * (that helper drags in a hooks chain, and this is pure data logic).
 */
const FLOORLESS_LISTING_TYPES: readonly ListingType[] = ["lot"];

const isFloorlessListingType = (listingType: ListingType): boolean =>
  FLOORLESS_LISTING_TYPES.includes(listingType);

export function getListingAddressRequirements(
  propertyType: string | undefined | null
): ListingAddressRequirements {
  const listingType = listingTypeFromPropertyType(propertyType);

  // Unknown property type → we can't tell whether the property has flats or
  // floors. Offer both as optional rather than demanding an apartment number
  // for a plot of land we failed to classify.
  if (!listingType) {
    return {
      street: "required",
      houseNumber: "required",
      apartmentNumber: "optional",
      floor: "optional",
      city: "required"
    };
  }

  return {
    street: "required",
    houseNumber: "required",
    apartmentNumber: showApartmentNumberInput(listingType) ? "required" : "hidden",
    floor: isFloorlessListingType(listingType) ? "hidden" : "optional",
    city: "required"
  };
}

/** Parts the realtor still has to fill before this listing can be sent. */
export function getMissingRequiredAddressParts(
  parts: ListingAddressParts,
  requirements: ListingAddressRequirements
): ListingAddressPartKey[] {
  return LISTING_ADDRESS_PART_KEYS.filter(
    (key) => requirements[key] === "required" && !parts[key].trim()
  );
}

/** Numeric-only parts — typed into number inputs and coerced back on read. */
const NUMERIC_PART_KEYS: ReadonlySet<ListingAddressPartKey> = new Set([
  "houseNumber",
  "apartmentNumber",
  "floor"
]);

export const isNumericAddressPart = (key: ListingAddressPartKey): boolean =>
  NUMERIC_PART_KEYS.has(key);

const toPartString = (value: string | number | undefined | null): string =>
  value === undefined || value === null ? "" : String(value);

/** The listing's own address, in form shape. */
export function buildAddressPartsFromListing(
  listing: ProcessedListingData | undefined | null
): ListingAddressParts {
  const address = listing?.address;
  return {
    street: toPartString(address?.street),
    houseNumber: toPartString(address?.houseNumber),
    apartmentNumber: toPartString(address?.apartmentNumber),
    floor: toPartString(address?.floor),
    city: toPartString(address?.city)
  };
}

/**
 * Listing values with the realtor's per-document edits laid on top. An absent
 * override key means "untouched, use the listing's value"; an override of `""`
 * means "the realtor cleared it on purpose for this document".
 */
export function mergeAddressParts(
  base: ListingAddressParts,
  override: Partial<ListingAddressParts> | undefined
): ListingAddressParts {
  if (!override) return base;
  const merged = { ...base };
  for (const key of LISTING_ADDRESS_PART_KEYS) {
    const value = override[key];
    if (value !== undefined) merged[key] = value;
  }
  return merged;
}

/**
 * May this user correct the listing RECORD (not just this document)?
 *
 * Only Webot-owned listings they own: a Yad2 or Madlan listing is a scrape of
 * someone else's data with no document of ours to write to, and another
 * realtor's Webot listing isn't ours to edit. Org-mates are excluded too —
 * shared visibility is a read grant, not an edit one.
 *
 * Ownership is keyed by EMAIL (`originalListingOwnersId` holds emails, despite
 * the name) — the same check the "my listings" filter uses.
 */
export function canEditListingRecord(
  listing: ProcessedListingData,
  userEmail: string | undefined | null
): boolean {
  if (!userEmail) return false;
  if (listing.listingOrigin !== "webot") return false;
  if (!listing.originalListingId) return false;
  return !!listing.originalListingOwnersId?.includes(userEmail);
}

/** `""` → `null` (clear the column), a number → itself, anything else → undefined (leave alone). */
const toRecordNumber = (value: string): number | null | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Address parts → the columns a `real-estate-listings` document actually
 * stores. The CRM record splits them differently from the processed listing:
 * `address` holds the street NAME, the house number lives in `streetNumber`,
 * and floor / apartment number are top-level columns.
 */
export function addressPartsToListingRecordFields(parts: ListingAddressParts): {
  address: string;
  streetNumber: number | null | undefined;
  apartmentNumber: number | null | undefined;
  floor: number | null | undefined;
  city: string;
} {
  return {
    address: parts.street.trim(),
    streetNumber: toRecordNumber(parts.houseNumber),
    apartmentNumber: toRecordNumber(parts.apartmentNumber),
    floor: toRecordNumber(parts.floor),
    city: parts.city.trim()
  };
}

/**
 * Address parts in the shape the formatters take: numeric parts as numbers,
 * empties dropped so a formatter never renders "קומה " with nothing after it.
 * `neighborhood` isn't editable in the send form (it's never contract-critical
 * and there's no clean way to validate it), so it rides through from the
 * listing untouched for the hidden-addresses addendum.
 */
export function addressPartsToFragments(
  parts: ListingAddressParts,
  neighborhood?: string | null
): {
  street?: string;
  houseNumber?: number;
  apartmentNumber?: number;
  floor?: number;
  neighborhood?: string;
  city?: string;
} {
  const num = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  };
  const text = (value: string): string | undefined => value.trim() || undefined;

  return {
    street: text(parts.street),
    houseNumber: num(parts.houseNumber),
    apartmentNumber: num(parts.apartmentNumber),
    floor: num(parts.floor),
    neighborhood: neighborhood?.trim() || undefined,
    city: text(parts.city)
  };
}
